import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api";
import { cld } from "@/lib/cld";
import { Section, Overline } from "@/components/Primitives";
import { ChevronRight, ArrowLeft } from "lucide-react";

export function BlogList() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const lang = i18n.language;

  useEffect(() => { api.get("/blog").then(r => setPosts(r.data || [])).catch(() => {}); }, []);
  const tx = (obj) => obj?.[lang] || obj?.fr || "";

  return (
    <div data-testid="blog-page" className="pt-32">
      <Section testId="blog-header">
        <Overline>{t("blog_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-6">{t("blog_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/70 leading-relaxed">{t("blog_page.intro")}</p>
      </Section>

      <Section testId="blog-list" className="!pt-0">
        {posts.length === 0 ? (
          <p className="text-[#F8F5EF]/50 italic">—</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, idx) => (
              <Link to={`/blog/${p.slug}`} key={p.id} className="luxe-card overflow-hidden group" data-testid={`blog-card-${idx}`}>
                {p.cover_url && <img loading="lazy" src={cld(p.cover_url, "card")} alt={tx(p.title)} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700" />}
                <div className="p-6">
                  <h3 className="font-serif-luxe text-2xl text-[#F8F5EF] mb-2">{tx(p.title)}</h3>
                  <p className="text-sm text-[#F8F5EF]/65 leading-relaxed">{tx(p.excerpt)}</p>
                  <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] inline-flex items-center gap-2">{t("blog_page.read_more")} <ChevronRight size={12} /></div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function BlogArticle() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  const lang = i18n.language;
  const tx = (obj) => obj?.[lang] || obj?.fr || "";

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setErr("404"));
  }, [slug]);

  if (err) return <Section><p className="pt-24 text-[#F8F5EF]/60">Article introuvable.</p></Section>;
  if (!post) return null;

  return (
    <div className="pt-32" data-testid="blog-article-page">
      <Section>
        <Link to="/blog" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-8" data-testid="blog-back">
          <ArrowLeft size={14} /> {t("blog_page.title")}
        </Link>
        <h1 className="font-serif-luxe text-4xl sm:text-5xl text-[#F8F5EF] mb-6">{tx(post.title)}</h1>
        {post.cover_url && <img src={cld(post.cover_url, "wide")} alt={tx(post.title)} className="w-full h-[420px] object-cover mb-10 border border-[#D4AF37]/20" />}
        <div className="prose-luxe text-[#F8F5EF]/85 leading-relaxed whitespace-pre-line">{tx(post.content)}</div>
      </Section>
    </div>
  );
}
